import { useEffect } from 'react';
import { useEntitySecurityPath } from '../../../auth/Security';
import { useDeepLinkRuntimeState } from './useDeepLinkRuntimeState';

/**
 * Keeps deep-link runtime item-id state in sync with UI selection state.
 *
 * It resolves the deep-link UI state path using `deepLinkUIStatePath` when provided,
 * otherwise it falls back to the current entity security path.
 *
 * Behavior:
 * - when `itemId` is truthy, sets the linked-item id at the resolved path
 * - when `itemId` is falsy, unsets the linked-item id at the resolved path
 */
export const useTrackDeepLinkItemId = (itemId?: string | null, deepLinkUIStatePath?: string): void => {
    const entitySecurityPath = useEntitySecurityPath();
    const { setDeepLinkItemId, unsetDeepLinkItemId } = useDeepLinkRuntimeState();

    useEffect(() => {
        const resolvedDeepLinkUIStatePath = deepLinkUIStatePath ?? entitySecurityPath;

        if (!resolvedDeepLinkUIStatePath) {
            return;
        }

        if (!itemId) {
            unsetDeepLinkItemId(resolvedDeepLinkUIStatePath);
            return;
        }

        setDeepLinkItemId(resolvedDeepLinkUIStatePath, itemId);
    }, [deepLinkUIStatePath, entitySecurityPath, itemId, setDeepLinkItemId, unsetDeepLinkItemId]);
};
